// Performance monitoring and Core Web Vitals optimization

// Lazy loading for images
function lazyLoadImages() {
  const images = document.querySelectorAll('img[data-src]');
  
  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        img.classList.remove('lazy');
        observer.unobserve(img);
      }
    });
  });

  images.forEach(img => imageObserver.observe(img));
}

// Critical CSS optimization
function loadNonCriticalCSS() {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = '../scss/non-critical.scss';
  link.media = 'print';
  link.onload = function() {
    this.media = 'all';
  };
  document.head.appendChild(link);
}

// Preload critical resources
function preloadCriticalResources() {
  const criticalResources = [
    { href: '../scss/main.scss', as: 'style' },
    { href: '../js/main.js', as: 'script' },
    { href: 'https://fonts.googleapis.com/css2?family=Fira+Code:wght@300;400;500;600;700&display=swap', as: 'style' }
  ];

  criticalResources.forEach(resource => {
    if (!document.querySelector(`link[href="${resource.href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource.href;
      link.as = resource.as;
      if (resource.as === 'style') {
        link.onload = function() {
          this.rel = 'stylesheet';
        };
      }
      document.head.appendChild(link);
    }
  });
}

// Core Web Vitals monitoring
function initCoreWebVitals() {
  // Monitor LCP (Largest Contentful Paint)
  new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      if (entry.entryType === 'largest-contentful-paint') {
        console.log('LCP:', entry.startTime);
      }
    }
  }).observe({ entryTypes: ['largest-contentful-paint'] });

  // Monitor FID (First Input Delay) - using polyfill approach
  new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      if (entry.entryType === 'first-input') {
        console.log('FID:', entry.processingStart - entry.startTime);
      }
    }
  }).observe({ entryTypes: ['first-input'] });

  // Monitor CLS (Cumulative Layout Shift)
  let clsValue = 0;
  new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      if (!entry.hadRecentInput) {
        clsValue += entry.value;
        console.log('CLS:', clsValue);
      }
    }
  }).observe({ entryTypes: ['layout-shift'] });
}

// Optimize font loading
function optimizeFontLoading() {
  // Use font-display: swap for better performance
  const fontLink = document.querySelector('link[href*="fonts.googleapis.com"]');
  if (fontLink && !fontLink.href.includes('display=swap')) {
    fontLink.href += '&display=swap';
  }
}

// Reduce JavaScript execution time
function optimizeJavaScriptExecution() {
  // Use requestIdleCallback for non-critical tasks
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      // Non-critical initialization tasks
      initAnalytics();
      setupServiceWorker();
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      initAnalytics();
      setupServiceWorker();
    }, 1000);
  }
}

// Analytics initialization (non-critical)
function initAnalytics() {
  // Placeholder for analytics code
  console.log('Analytics initialized');
}

// Service Worker setup for caching
function setupServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register(new URL('../sw.js', import.meta.url)).then(registration => {
      console.log('ServiceWorker registration successful');
    }).catch(error => {
      console.log('ServiceWorker registration failed');
    });
  }
}

// Resource hints optimization
function addResourceHints() {
  // DNS prefetch for external domains
  const externalDomains = ['fonts.googleapis.com', 'fonts.gstatic.com', 'unpkg.com'];
  
  externalDomains.forEach(domain => {
    if (!document.querySelector(`link[href="https://${domain}"]`)) {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = `https://${domain}`;
      document.head.appendChild(link);
    }
  });
}

// Image optimization
function optimizeImages() {
  const images = document.querySelectorAll('img');
  
  images.forEach(img => {
    // Add loading="lazy" to images below the fold
    if (!img.hasAttribute('loading')) {
      const rect = img.getBoundingClientRect();
      if (rect.top > window.innerHeight) {
        img.setAttribute('loading', 'lazy');
      }
    }
    
    // Add proper alt text if missing
    if (!img.hasAttribute('alt')) {
      img.setAttribute('alt', 'Изображение портфолио Никиты Карасёва');
    }
    
    // Add width and height to prevent layout shifts
    if (!img.hasAttribute('width') || !img.hasAttribute('height')) {
      img.addEventListener('load', function() {
        if (!this.hasAttribute('width')) {
          this.setAttribute('width', this.naturalWidth);
        }
        if (!this.hasAttribute('height')) {
          this.setAttribute('height', this.naturalHeight);
        }
      });
    }
  });
}

// Initialize all performance optimizations
function initPerformanceOptimizations() {
  // Run critical optimizations immediately
  optimizeFontLoading();
  addResourceHints();
  preloadCriticalResources();
  
  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      lazyLoadImages();
      optimizeImages();
      initCoreWebVitals();
    });
  } else {
    lazyLoadImages();
    optimizeImages();
    initCoreWebVitals();
  }
  
  // Run non-critical optimizations after page load
  window.addEventListener('load', () => {
    optimizeJavaScriptExecution();
    loadNonCriticalCSS();
  });
}

// Export for use in main.js
export { initPerformanceOptimizations };

// Auto-initialize
initPerformanceOptimizations();